// components/ResetDialog.jsx

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/config';
import PropTypes from 'prop-types';

/**
 * ResetDialog Component
 * 
 * Props:
 * - open (boolean): Controls the visibility of the dialog.
 * - onClose (function): Function to close the dialog.
 * - deviceCode (string): The device code to identify which controller to reset.
 * - subId (string | number): The sub ID of the sensor.
 * - onResetSuccess (function): Callback function to handle successful reset (e.g., updating UI).
 * - userPermission (string): User's permission level to control access.
 */
const ResetDialog = ({ open, onClose, deviceCode, subId, onResetSuccess, userPermission }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Handles the reset action by making an API call to the reset endpoint.
   */
  const handleReset = async () => {
    // Check for 'edit' permission before proceeding
    if (userPermission !== 'edit') {
      setError('You do not have permission to perform a reset.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Construct the API endpoint with deviceCode
      const endpoint = `${API_BASE_URL}api/sensor/${deviceCode}/reset`;

      // Make the POST request with subId in the request body
      const response = await axios.post(endpoint, { subId });

      // Destructure the necessary data from the response
      const { resetDuration, message } = response.data;

      // Invoke the success callback with resetDuration and message
      onResetSuccess(resetDuration, message);

      // Close the dialog
      onClose();
    } catch (err) {
      console.error('Error performing reset:', err);

      // Determine the error message
      let errorMsg = 'Failed to perform reset. Please try again.';
      if (err.response && err.response.data && err.response.data.message) {
        errorMsg = err.response.data.message;
      }

      // Set the error state to display in the dialog
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Confirm Reset</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Do you really want to reset the controller? This action will initiate a reset operation.
        </DialogContentText>
        {error && (
          <DialogContentText color="error" sx={{ mt: 2 }}>
            {error}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleReset} color="error" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Confirm Reset'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// PropTypes for type checking
ResetDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  deviceCode: PropTypes.string.isRequired,
  subId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onResetSuccess: PropTypes.func.isRequired,
  userPermission: PropTypes.string.isRequired,
};

export default ResetDialog;